import { negotiateVisitorLocale, type VisitorLocale } from '@destera/i18n';

/**
 * Widget yapılandırması: gömme kodundaki `data-*` özniteliklerinden okunur.
 * Yükleyici ve sohbet penceresi aynı tipi paylaşır (DRY).
 *
 * <script async src="https://cdn…/loader.js" data-workspace="…" data-locale="tr"></script>
 */
export type WidgetConfig = {
  readonly workspaceId: string;
  readonly apiBase: string;
  readonly locale: VisitorLocale;
  readonly position: 'right' | 'left';
  readonly accent: string;
  /** Olay toplama servisinin kökü; TR'de sonlanır (MIMARI §6.2). */
  readonly ingestBase: string;
  /** Ziyaretçi izleme açık mı? İşletme kapatabilir; izin modundan bağımsızdır. */
  readonly track: boolean;
  /** Sunucusuz geliştirme/önizleme modu: bellekte yankılanan taşıma katmanı. */
  readonly demo: boolean;
  /**
   * İşletmenin panelden ayarladığı marka; sunucudan geliyor ve gömme koduna YAZILMIYOR.
   *
   * Gömme koduna yazsaydık rengi değiştiren işletmenin kodu yeniden yapıştırması gerekirdi.
   * Yokluğu geçerli bir hâl: ağ düşse de widget ürün varsayılanıyla çiziliyor.
   */
  readonly brand?: WidgetBrand | undefined;
};

/** Sorunlar sitenin geliştiricisine konsolda gösterilir; bu yüzden İngilizcedir (son kullanıcı görmez). */
/** Markanın widget tarafındaki hâli; sözleşmedeki `WidgetBrand` ile aynı alanlar. */
export type WidgetBrand = {
  readonly accent: string;
  readonly title?: string | undefined;
  readonly greeting?: string | undefined;
  readonly quickReplies: readonly string[];
  readonly launcherLabel?: string | undefined;
  readonly logoUrl?: string | undefined;
  /** "Destera ile" bağlantısı gösterilecek mi; kararı sunucu veriyor, widget paketi bilmiyor. */
  readonly showBranding: boolean;
  /**
   * İşletmenin yazdığı CSS; GÖLGE KÖKE giriyor, sayfaya değil.
   *
   * Paket kapısı sunucuda: alan burada varsa hakkı da vardır. Widget paketi bilmiyor ve
   * bilmemeli — fiyat tablosunu her ziyaretçinin tarayıcısına indirmek olurdu.
   */
  readonly customCss?: string | undefined;
  /**
   * STİL TOKENLARI (§3.7.3 "Stil"); adlar, piksel değil.
   *
   * Tipleri DİZE (daraltılmış birlik değil) çünkü değer sunucudan geliyor ve eski bir
   * widget yeni bir adı görebilir: tema tablosu tanınmayan adı varsayılana düşürüyor.
   * Daraltmak, derleyiciye veriyi doğrulamış gibi davranmak olurdu.
   */
  readonly corner?: string | undefined;
  readonly shadow?: string | undefined;
  readonly font?: string | undefined;
  /** Balonun köşesi (`right`/`left`); gömme kodundaki değeri ezer, tanınmayan ad yedeğe düşer. */
  readonly position?: string | undefined;
};

export type ConfigResult =
  | { readonly ok: true; readonly config: WidgetConfig }
  | { readonly ok: false; readonly problems: readonly string[] };

const HEX_COLOR = /^#[0-9a-f]{6}$/i;
const DEFAULT_ACCENT = '#4f46e5';

/** `read` bir öznitelik okuyucudur: gömme kodunda `(k) => script.dataset[k]`. */
export type AttributeReader = (name: string) => string | undefined;

/**
 * Balonun köşesi: tanınan ad ya da yedek. Gömme kodu (`data-position`) ve marka aynı kuraldan
 * geçiyor; marka geldiğinde gömme kodunu EZİYOR — köşeyi panelden değiştiren işletmenin sitesine
 * dokunması gerekmesin.
 */
export const positionOf = (
  value: string | undefined,
  fallback: WidgetConfig['position'],
): WidgetConfig['position'] => (value === 'left' || value === 'right' ? value : fallback);

export function readConfig(read: AttributeReader, browserLocales: readonly string[]): ConfigResult {
  const problems: string[] = [];
  const workspaceId = read('workspace')?.trim() ?? '';
  if (!workspaceId) problems.push('data-workspace is required');

  const accent = read('accent') ?? DEFAULT_ACCENT;
  if (!HEX_COLOR.test(accent)) problems.push('data-accent must be a #rrggbb color');

  if (problems.length > 0) return { ok: false, problems };
  return {
    ok: true,
    config: {
      workspaceId,
      apiBase: read('api') ?? import.meta.env.VITE_API_BASE ?? 'https://api.destera.app',
      ingestBase: read('ingest') ?? import.meta.env.VITE_INGEST_BASE ?? 'https://t.destera.app',
      track: read('track') !== 'false',
      locale: negotiateVisitorLocale([read('locale'), ...browserLocales]),
      position: positionOf(read('position'), 'right'),
      accent,
      demo: read('demo') === 'true',
    },
  };
}
